<template>
  <a
    class="product-tile widget"
    :href="url"
    :id="id"
    tabindex="0"
    aria-live="polite"
  >
    <div class="product-tile__images">
      <a class="product-tile widget" :href="url" tabindex="-1">
        <img
          class="product-tile__img-main"
          :src="imageSize(images.default, '304x')"
          :alt="images.defaultAlt"
          :srcset="
            `${imageSize(images.default, '168x')} 168w, ${imageSize(
              images.default,
              '304x'
            )} 304w, ${imageSize(images.default, '456x')} 1.5x`
          "
        />
        <img
          class="product-tile__img-hover"
          :src="imageSize(images.hover, '304x')"
          :alt="images.hoverAlt"
          :srcset="
            `${imageSize(images.hover, '168x')} 168w, ${imageSize(
              images.hover,
              '304x'
            )} 304w, ${imageSize(images.hover, '456x')} 1.5x`
          "
        />
      </a>
      <product-featured-badges v-if="product.featuredBadges" :badges="product.featuredBadges" />
    </div>
    <div class="product-tile__static-text-content" :class="textAlignment">
      <div class="row">
        <div class="col-12-sm product-tile__title color-black">
          <span class="product-tile-title">{{ staticProductTitle }}</span>
        </div>
        <div class="col-12-sm product-tile__color color-off-black">
          <span class="product-tile-color body-sm">{{
            staticProductColor
          }}</span>
        </div>
        <div
          class="col-12-sm product-tile__type color-grey"
          v-if="product.product_fit || product.product_type"
        >
          <span class="product-tile-type body-sm">{{ staticProductType }}</span>
        </div>
      </div>
      <div class="row">
        <div class="product-tile__price-container" v-if="!product.hidePrice">
          <span class="product-tile__compare-price" v-if="onSale">
            <s>{{ variant.compare_at_price | money }}</s>
          </span>
          <span
            class="product-tile__price"
            :class="{ 'color-util-sale': onSale }"
            >{{ variant.price | money }}</span
          >
        </div>
        <product-reviews-badge
          :product="product"
          :settings="settings"
        ></product-reviews-badge>
      </div>
    </div>
  </a>
</template>

<script>
import ProductReviewsBadge from '@/scripts/components/product/ProductReviewsBadge.vue'
import ProductFeaturedBadges from './ProductFeaturedBadges.vue';

import {
  imageSize,
  getProductImageAlt,
  getProductImageHover
} from '@/scripts/helpers/util'
import { withId } from '@/scripts/helpers/props'
import uniqBy from 'lodash/uniqBy'

/**
 * @typedef ImageSource
 * @type {Object}
 * @prop {String} default
 * @prop {String} defaultAlt
 * @prop {String} hover
 * @prop {String} hoverAlt
 *
 * @typedef Buttons
 * @type {Object}
 * @prop {Boolean} showOptions
 * @prop {Boolean} showAtc
 */

export default {
  name: 'ProductTileWidget',
  data () {
    return {
      variant:
        this.selectedVariant ||
        this.product.selected_or_first_available_variant,
      loaded: false
    }
  },
  components: {
    ProductReviewsBadge,
    ProductFeaturedBadges,
  },
  props: {
    product: Object,
    onAddToCart: Function, // if not provided, the atc will not show
    collectionHandle: String, // used for linking to pdp with proper collection
    textAlignment: String,
    truncateTitle: {
      type: Boolean,
      default: true
    },
    settings: Object,
    ...withId()
  },
  filters: {
    truncated (s) {
      if (s.length > 42) {
        return `${s.substring(0, 42)}...`
      }
      return s
    }
  },
  methods: {
    imageSize
  },
  watch: {
    selectedVariant (n, o) {
      if (n.id !== o.id) {
        this.variant = n
      }
    },
    product (n, o) {
      if (n.id !== o.id) {
        this.variant = n.selected_or_first_available_variant
      }
    }
  },
  computed: {
    /** @returns {String} */
    url () {
      let urlParts = []

      if (!this.product.handle) {
        if (this.product.url) return this.product.url
      }

      if (this.collectionHandle)
        urlParts.push(`/collections/${this.collectionHandle}`)

      urlParts.push(`/products/${this.product.handle}`)

      // only add variant if we're not the default
      if (this.product.variants.map(v => v.id)[0] !== this.variant.id)
        urlParts.push(`?variant=${this.variant.id}`)

      return urlParts.join('')
    },

    /** @returns {ImageSource} */
    images () {
      const defaultImage = this.product.images[0]
      const defaultImageAlt = this.product.title
      const nextImage = this.product.images[1]
        ? this.product.images[1]
        : this.product.images[0]
      const nextImageAlt = this.product.title

      return {
        default: defaultImage.src,
        defaultAlt: defaultImageAlt,
        hover: nextImage.src,
        hoverAlt: nextImageAlt
      }
    },

    /** @returns {Array<Object>} */
    filteredVariants () {
      return this.product.variants.filter(
        v => v.title.toLowerCase() !== 'default title'
      )
    },

    filteredColorVariants () {
      if (!this.hasColorVariants) return []

      return uniqBy(this.product.variants, `option${this.colorIndex + 1}`)
    },

    /** @returns {Boolean} */
    onSale () {
      return (
        this.variant.available &&
        this.variant.compare_at_price &&
        this.variant.price < this.variant.compare_at_price
      )
    },

    /** @returns {String} */
    staticProductTitle () {
      const t = this.product.title
      const productTitle = t.substr(0, t.lastIndexOf('-'))
      return this.truncateTitle
        ? this.$options.filters.truncated(productTitle)
        : productTitle
    },

    /** @returns {String} */
    staticProductColor () {
      return this.product.title
        .split('-')
        .pop()
        .trim()
    },

    staticProductType () {
      if (this.product.product_fit) return this.product.product_fit

      return this.product.product_type
    }
  }
}
</script>

<style scoped lang="scss">
.product-tile {
  position: relative;
  background-color: $color-white;
  user-select: none;
  display: flex;
  flex-direction: column;
  color: inherit;
  cursor: pointer;

  @include ie {
    display: block;
  }

  /* Fixes iOS :hover issue where it ignores click */
  @media (hover: hover) {
    &:hover &__img-hover,
    &:focus &__img-hover {
      opacity: 1;
    }
  }

  span:not(.button__content) {
    display: inline-block;
  }

  .row > * {
    margin-bottom: 0;
  }

  .button-row {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0 8px;
    @include mobile-tablet {
      .button__content {
        font-size: 12px;
      }
    }
  }

  &__price {
    @include product-tile-price;
  }

  @media screen and (max-width: 400px) {
    max-width: 100%;
  }

  &:focus {
    // outline: none !important;
  }

  a {
    line-height: normal;
  }

  &-title {
    font-size: rem(14);
    line-height: 1em;
  }

  &-color {
    font-size: rem(12);
    line-height: 1em !important;
  }

  &__images {
    position: relative;
    font-size: 0;
    height: 0;
    width: 100%;
    padding-top: 150%;
    background-color: $color-white;
  }

  &__images a {
    font-size: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__img-hover {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.2s linear;
  }

  &__img-main {
    z-index: 2;
    position: relative;
    transition: none;
    transition: opacity 0.2s linear;
  }

  &__img-hover {
    z-index: 5;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
    z-index: 4;
    fill: $color-lightest-grey;
  }

  &__img-main,
  &__img-hover {
    width: 100%;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;
  }

  &__static-text-content {
    background-color: $color-white;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
  }

  &__compare-price {
    padding-left: 8px;
  }

  &__compare-price,
  &__compare-price s {
    @include product-tile-price;
  }
}
</style>
